import { useState } from 'react';
// Lazy-load PDF export to keep bundle small
async function handleExportPdf(analysis: any, emailBody: string, emailSubject: string, analysisMode: 'shield' | 'sender') {
  const { exportAnalysisPdf } = await import('../services/export-pdf.js');
  exportAnalysisPdf(analysis, emailBody, emailSubject, analysisMode);
}

interface Category {
  category: string;
  severity: string;
  confidence: number;
  evidence: string;
  translation: string;
}

interface AnalysisResult {
  toxicity_score: number;
  categories: Category[];
  overall_assessment: string;
  suggested_diplomatic_reply: string | null;
  constructive_rewrite: string | null;
}

type AnalysisMode = 'shield' | 'sender';

// --- Score helpers ---

function scoreColor(score: number) {
  if (score <= 20) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Clean' };
  if (score <= 40) return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'Mild' };
  if (score <= 60) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Warning' };
  if (score <= 75) return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Toxic' };
  return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Critical' };
}

function severityColor(severity: string) {
  switch (severity) {
    case 'critical': return 'border-red-200 bg-red-50 text-red-700';
    case 'high': return 'border-orange-200 bg-orange-50 text-orange-700';
    case 'medium': return 'border-yellow-200 bg-yellow-50 text-yellow-700';
    default: return 'border-surface-200 bg-surface-100 text-ink-muted';
  }
}

function formatCategory(cat: string) {
  return cat.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

function highlightBody(body: string, categories: Category[], isSender: boolean) {
  let result = body;
  const markClass = isSender
    ? 'bg-emerald-100 text-emerald-800 rounded px-0.5'
    : 'bg-red-100 text-red-800 rounded px-0.5';
  for (const cat of categories) {
    if (cat.evidence && cat.evidence.length > 5) {
      const escaped = cat.evidence.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(
        new RegExp(`(${escaped})`, 'gi'),
        `<mark class="${markClass}">$1</mark>`
      );
    }
  }
  return result;
}

const SHIELD_PLACEHOLDER = `As per my last email (and the two before that), I still haven't received the Q3 numbers.

I've copied your director on this as I want to make sure we're all aligned on timelines.

For future reference, when I set a deadline, I expect it to be met.`;

const SENDER_PLACEHOLDER = `Hey,

I still haven't gotten the Q3 numbers from you. I asked for these last week and the week before.

I'm adding your manager so everyone knows what's going on with the timeline.

Going forward, when I give you a deadline, please stick to it.`;

export function TryPage({ token }: { token: string }) {
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<AnalysisMode>('shield');

  async function handleAnalyze() {
    const text = body.trim();
    if (!text) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? 'https://api-staging.aasshield.com'}/api/v1/analyze`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            subject: subject || '(no subject)',
            bodyText: text,
            senderHash: 'try-sandbox',
            messageId: `try-${Date.now()}`,
            platform: 'web',
            ccCount: 0,
            ccIncludesManagement: false,
            analysisMode: mode,
          }),
        },
      );

      const json = await res.json();
      if (!res.ok) {
        setError(json.error?.message ?? `Error ${res.status}`);
      } else {
        setResult(json.data);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleModeSwitch(newMode: AnalysisMode) {
    if (newMode !== mode) {
      setMode(newMode);
      setResult(null);
      setError('');
    }
  }

  const style = result ? scoreColor(result.toxicity_score) : null;
  const isClean = result && result.toxicity_score <= 20;
  const isSender = mode === 'sender';

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-bold text-ink">Try it</h1>
          <p className="text-sm text-ink-muted mt-1">
            {isSender
              ? 'Check your draft before sending. Nothing is stored.'
              : 'Paste an email and see how AAS reads it. Nothing is stored.'
            }
          </p>
        </div>

        {/* Mode toggle */}
        <div className="inline-flex items-center rounded-full border border-surface-200 bg-white p-1">
          <button
            onClick={() => handleModeSwitch('shield')}
            className={`relative rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${
              mode === 'shield' ? 'text-ink' : 'text-ink-light hover:text-ink-muted'
            }`}
          >
            {mode === 'shield' && (
              <span className="absolute inset-0 rounded-full bg-red-500/15 border border-red-500/25" />
            )}
            <span className="relative flex items-center gap-1.5">
              <svg viewBox="0 0 16 16" fill="none" className={`h-3.5 w-3.5 ${mode === 'shield' ? 'text-red-600' : ''}`}>
                <path d="M8 1.5L3 4v3.5c0 3.4 2.13 6.6 5 7.5 2.87-.9 5-4.1 5-7.5V4L8 1.5z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.15" />
              </svg>
              Shield
            </span>
          </button>
          <button
            onClick={() => handleModeSwitch('sender')}
            className={`relative rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${
              mode === 'sender' ? 'text-ink' : 'text-ink-light hover:text-ink-muted'
            }`}
          >
            {mode === 'sender' && (
              <span className="absolute inset-0 rounded-full bg-emerald-500/15 border border-emerald-500/25" />
            )}
            <span className="relative flex items-center gap-1.5">
              <svg viewBox="0 0 16 16" fill="none" className={`h-3.5 w-3.5 ${mode === 'sender' ? 'text-emerald-600' : ''}`}>
                <path d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Sender
            </span>
          </button>
        </div>
      </div>

      {/* Input area */}
      <div className="rounded-xl border border-surface-200 bg-white p-5 mb-6">
        <input
          type="text"
          placeholder="Subject (optional)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border-b border-surface-200 bg-transparent px-1 py-2 text-sm font-medium text-ink placeholder:text-surface-300 focus:outline-none focus:border-ink-light mb-3"
        />
        <textarea
          placeholder={isSender ? SENDER_PLACEHOLDER : SHIELD_PLACEHOLDER}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          className="w-full bg-transparent px-1 text-sm text-ink font-mono leading-relaxed placeholder:text-surface-300 focus:outline-none resize-none"
        />
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-100">
          <p className="text-xs text-surface-300">Content is analyzed in transit and never stored</p>
          <button
            onClick={handleAnalyze}
            disabled={loading || !body.trim()}
            className={`rounded-lg px-5 py-2 text-sm font-medium text-white disabled:opacity-30 transition ${
              isSender ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-brand hover:bg-red-700'
            }`}
          >
            {loading ? 'Analyzing...' : isSender ? 'Check Draft' : 'Analyze'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && style && (
        <div className="space-y-4">

          {/* Score bar */}
          <div className={`rounded-xl border ${style.border} bg-white overflow-hidden`}>
            <div className="flex items-center gap-4 px-5 py-4">
              <div className={`shrink-0 w-16 h-16 rounded-xl ${style.bg} flex flex-col items-center justify-center`}>
                <span className={`text-2xl font-black tabular-nums leading-none ${style.text}`}>
                  {result.toxicity_score}
                </span>
                <span className={`text-[10px] font-medium ${style.text} mt-0.5`}>/100</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`rounded-full ${style.bg} ${style.text} px-2.5 py-0.5 text-xs font-bold`}>
                    {style.label}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    isSender
                      ? 'bg-sky-50 text-sky-700 border border-sky-200'
                      : 'bg-surface-100 text-ink-muted border border-surface-200'
                  }`}>
                    {isSender ? 'Sender check' : 'Shield scan'}
                  </span>
                  {result.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {result.categories.map((cat) => (
                        <span key={cat.category} className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${severityColor(cat.severity)}`}>
                          {formatCategory(cat.category)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {result.overall_assessment && (
                  <p className="text-sm text-ink-muted mt-1.5 leading-snug line-clamp-2">
                    {result.overall_assessment}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Email body with highlights */}
          <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-surface-100 flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isSender ? 'bg-sky-400' : 'bg-red-400'}`} />
              <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
                {isClean
                  ? (isSender ? 'Your draft' : 'Your email')
                  : (isSender ? 'What AAS flagged in your draft' : 'What AAS found')
                }
              </p>
            </div>
            <div
              className="px-5 py-4 font-mono text-[13px] text-ink leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: highlightBody(body, result.categories, isSender) }}
            />
          </div>

          {/* Evidence breakdown */}
          {result.categories.length > 0 && (
            <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-surface-100">
                <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
                  {isSender ? 'How the recipient might read this' : 'What they really mean'}
                </p>
              </div>
              <div className="divide-y divide-surface-100">
                {result.categories.map((cat) => (
                  <div key={cat.category} className="px-5 py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${severityColor(cat.severity)}`}>
                        {formatCategory(cat.category)}
                      </span>
                      <span className="text-xs text-surface-300">{Math.round(cat.confidence * 100)}%</span>
                    </div>
                    {cat.evidence && (
                      <p className={`text-sm font-mono rounded-lg px-3 py-2 mb-2 ${
                        isSender ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'
                      }`}>
                        &ldquo;{cat.evidence}&rdquo;
                      </p>
                    )}
                    {cat.translation && (
                      <p className="text-sm text-ink-muted italic pl-3 border-l-2 border-surface-200">
                        {cat.translation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Constructive rewrite */}
          {result.constructive_rewrite && (
            <div className="rounded-xl border border-sky-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-sky-100 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-sky-400" />
                <p className="text-xs font-medium text-sky-700 uppercase tracking-wide">
                  {isSender ? 'Better version of your email' : 'How it should have been written'}
                </p>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">
                  {result.constructive_rewrite}
                </p>
                <button
                  onClick={() => navigator.clipboard.writeText(result.constructive_rewrite!)}
                  className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100 transition"
                >
                  {isSender ? 'Use this version' : 'Copy to clipboard'}
                </button>
              </div>
            </div>
          )}

          {/* Diplomatic reply — only in shield mode */}
          {!isSender && result.suggested_diplomatic_reply && (
            <div className="rounded-xl border border-emerald-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-emerald-100 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Suggested reply</p>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">
                  {result.suggested_diplomatic_reply}
                </p>
                <button
                  onClick={() => navigator.clipboard.writeText(result.suggested_diplomatic_reply!)}
                  className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition"
                >
                  Copy to clipboard
                </button>
              </div>
            </div>
          )}

          {/* Clean result */}
          {isClean && (
            <div className={`rounded-xl border p-5 text-center ${
              isSender ? 'border-sky-200 bg-sky-50' : 'border-emerald-200 bg-emerald-50'
            }`}>
              <p className={`text-sm font-medium ${isSender ? 'text-sky-800' : 'text-emerald-800'}`}>
                {isSender
                  ? 'Your draft looks great! Professional and respectful tone.'
                  : 'No toxic patterns detected. This email is professional and respectful.'
                }
              </p>
            </div>
          )}

          {/* Export PDF */}
          <div className="flex justify-end">
            <button
              onClick={() => handleExportPdf(result, body, subject, mode)}
              className="flex items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-4 py-2 text-xs font-medium text-ink-muted hover:text-ink hover:border-surface-300 transition"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export PDF
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && !loading && !error && (
        <div className="rounded-xl border border-dashed border-surface-200 p-12 text-center">
          <p className="text-sm text-surface-300">
            {isSender
              ? 'Write your draft above and click Check Draft'
              : 'Paste an email above and click Analyze'
            }
          </p>
        </div>
      )}
    </div>
  );
}
